import React, { useState, useEffect, useRef, useCallback } from 'react';

// ==================== Image attach (shared by both chat surfaces) ====================
const MAX_IMAGE_MB = 10;

export function useImageAttach() {
  const [attachments, setAttachments] = useState([]); // { file, previewUrl }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const pickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const room = Math.max(0, 5 - attachments.length);
    const next = files.slice(0, room).map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setAttachments((prev) => [...prev, ...next]);
  };

  const handleFileChange = (e) => {
    pickFiles(e.target.files);
    e.target.value = '';
  };

  const removeAttachment = (i) => {
    setAttachments((prev) => {
      URL.revokeObjectURL(prev[i].previewUrl);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  const clearAttachments = () => {
    attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    setAttachments([]);
  };

  /** Upload all attachments to the media library; returns array of /uploads/... paths */
  const uploadAll = async (signal) => {
    if (attachments.length === 0) return [];
    setUploading(true);
    try {
      const urls = [];
      for (const att of attachments) {
        if (att.file.size > MAX_IMAGE_MB * 1024 * 1024) {
          throw new Error(`Image exceeds ${MAX_IMAGE_MB}MB limit: ${att.file.name}`);
        }
        const formData = new FormData();
        formData.append('file', att.file);
        const res = await fetch(`${API_URL}/uploads?folder=messages`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
          signal,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Upload failed (${res.status})`);
        }
        const data = await res.json();
        urls.push(data.url);
      }
      return urls;
    } finally {
      setUploading(false);
    }
  };

  return { attachments, uploading, fileInputRef, pickFiles, handleFileChange, removeAttachment, clearAttachments, uploadAll };
}
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const MESSAGES_PER_PAGE = 25;

/**
 * Reusable chat component for displaying and replying to a conversation.
 * Used in both Messages page and Customer Dashboard Messages tab.
 *
 * @param {string} conversationSid - The Twilio conversation SID (optional — if missing, shows empty chat with ability to start)
 * @param {string} phoneNumber - The customer phone number
 * @param {string} customerName - Display name for the customer
 * @param {number} [customerId] - The customer ID (used to link conversation when created)
 * @param {number} [height] - Optional fixed height for the message container
 * @param {function} [onConversationStarted] - Callback when a new conversation is created
 */
export default function MessageChat({ conversationSid, phoneNumber, customerName, customerId, height, onConversationStarted }) {
  const navigate = useNavigate();
  const messagesContainerRef = useRef(null);
  const selectedThreadRef = useRef(null);
  const socket = useSocket();

  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [scrollPositionTracked, setScrollPositionTracked] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const { attachments, uploading, fileInputRef, pickFiles, handleFileChange, removeAttachment, clearAttachments, uploadAll } = useImageAttach();

  // Keep ref in sync for socket handler
  useEffect(() => {
    selectedThreadRef.current = { conversation_sid: conversationSid };
  }, [conversationSid]);

  // Socket.io listener for real-time new messages
  useEffect(() => {
    if (!socket) return;

    socket.on('new-message', async (data) => {
      const currentThread = selectedThreadRef.current;
      if (currentThread && currentThread.conversation_sid === data.conversationSid) {
        const newMessage = {
          sid: `socket-${Date.now()}`,
          index: displayedMessages.length > 0 ? (displayedMessages[displayedMessages.length - 1].index || 0) + 1 : 0,
          author: data.customerPhone ? 'Customer' : 'Store',
          body: data.body || '(Image)',
          dateCreated: new Date(data.timestamp).toISOString(),
        };
        setDisplayedMessages(prev => [...prev, newMessage]);
        // Scroll to bottom to show new message
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 100);
      }
    });

    return () => {
      socket.off('new-message');
    };
  }, [socket]);

  const fetchMessages = async (beforeIndex) => {
    try {
      let url = `${API_URL}/conversations/messages/${conversationSid}?limit=${MESSAGES_PER_PAGE}`;
      if (beforeIndex !== undefined && beforeIndex !== null) {
        url += `&beforeIndex=${beforeIndex}`;
      }
      const res = await fetch(url, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        return data.messages;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      return [];
    }
  };

  const loadInitialMessages = async () => {
    const fetchedMessages = await fetchMessages();
    setDisplayedMessages(fetchedMessages);
    setHasMore(fetchedMessages.length === MESSAGES_PER_PAGE);
    setScrollPositionTracked(false);

    // Scroll to bottom to show newest messages
    setTimeout(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    }, 100);
  };

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || !scrollPositionTracked || displayedMessages.length === 0) return;

    const container = messagesContainerRef.current;
    const scrollHeightBefore = container.scrollHeight;

    setLoadingMore(true);

    const oldestMessage = displayedMessages[0];
    const beforeIndex = oldestMessage?.index;

    const olderMessages = await fetchMessages(beforeIndex);

    if (olderMessages.length > 0) {
      setDisplayedMessages(prev => [...olderMessages, ...prev]);
      setHasMore(olderMessages.length === MESSAGES_PER_PAGE);

      // Maintain scroll position after prepending older messages
      requestAnimationFrame(() => {
        const scrollHeightDiff = container.scrollHeight - scrollHeightBefore;
        container.scrollTop = scrollHeightDiff;
      });
    }

    setLoadingMore(false);
  };

  const handleScroll = (e) => {
    const { scrollTop } = e.target;
    if (scrollTop < 50) {
      setScrollPositionTracked(true);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();

    if (!replyText.trim() && attachments.length === 0) return;
    if (sending || uploading) return;

    setSending(true);
    const controller = new AbortController();

    try {
      let currentConversationSid = conversationSid;

      // If no conversation exists, create one first
      if (!currentConversationSid && phoneNumber) {
        const startRes = await fetch(`${API_URL}/conversations/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            phoneNumber: phoneNumber,
            customerName: customerName,
            customer_id: customerId
          })
        });

        if (!startRes.ok) {
          const data = await startRes.json();
          throw new Error(data.error || 'Failed to start conversation');
        }

        const data = await startRes.json();
        currentConversationSid = data.conversationSid;
        if (onConversationStarted) {
          onConversationStarted(currentConversationSid);
        }
      }

      if (!currentConversationSid) {
        alert('Unable to send message — no conversation available.');
        return;
      }

      // Upload any attached images first (media library), then send with their URLs
      const mediaUrls = attachments.length > 0 ? await uploadAll(controller.signal) : [];

      const res = await fetch(`${API_URL}/conversations/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          conversationSid: currentConversationSid,
          message: replyText.trim(),
          mediaUrls
        })
      });

      if (res.ok) {
        setReplyText('');
        clearAttachments();
        if (currentConversationSid) {
          await loadInitialMessages();
        }
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert(error.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDateTime = (datetime, showYear = false) => {
    const date = new Date(datetime);
    const currentYear = new Date().getFullYear();
    const messageYear = date.getFullYear();
    const shouldShowYear = showYear || messageYear !== currentYear;

    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };

    if (shouldShowYear) {
      options.year = 'numeric';
    }

    return date.toLocaleString('en-US', options);
  };

  // Load messages when conversationSid changes
  useEffect(() => {
    if (conversationSid) {
      loadInitialMessages();
    }
  }, [conversationSid]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 bg-gray-50 border-b flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-700 flex items-center gap-1">
            <span>Chat with</span>
            {customerId ? (
              <span
                onClick={() => navigate(`/customers/${customerId}/dashboard`)}
                className="cursor-pointer text-blue-600 hover:underline hover:text-blue-800"
              >
                {customerName || 'unknown'}
              </span>
            ) : (
              <span>{customerName || 'unknown'}</span>
            )}
            <span>({phoneNumber})</span>
          </h3>
        </div>
      </div>

      {/* Messages Container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="overflow-y-auto flex-1 bg-gray-100 p-4"
        style={height ? { height: height } : undefined}
      >
        {hasMore && scrollPositionTracked && (
          <div className="flex justify-center mb-4">
            <button
              onClick={loadMoreMessages}
              disabled={loadingMore}
              className="bg-white text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? 'Loading...' : 'Load older messages'}
            </button>
          </div>
        )}

        {displayedMessages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No messages yet
          </div>
        ) : (
          displayedMessages.map((message, index) => (
            <div
              key={message.sid || index}
              className={`flex mb-3 ${
                message.author === 'Store' ? 'justify-end' : 'justify-start'
              }`}
            >
              <div
                className={`max-w-xs md:max-w-md lg:max-w-lg rounded-2xl overflow-hidden ${
                  message.author === 'Store' ? 'rounded-tr-sm' : 'rounded-tl-sm'
                }`}
              >
                {/* Sender name header bar */}
                {(message.senderUser || message.author !== 'Store') && (
                  <div
                    className={`px-3 py-1 text-xs font-medium border-b ${
                      message.author === 'Store'
                        ? 'bg-blue-600 text-blue-100 border-blue-700'
                        : 'bg-gray-100 text-gray-500 border-gray-200'
                    }`}
                  >
                    {message.senderUser
                      ? message.senderUser.userName
                      : message.author === 'Store'
                        ? 'Store'
                        : 'Customer'}
                  </div>
                )}
                <div
                  className={`px-4 py-2 ${
                    message.author === 'Store'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-800'
                  }`}
                >
                  {message.body && (
                    <div className="text-sm break-words">
                      {message.body}
                    </div>
                  )}
                  {(() => {
                    if (message.numMedia && parseInt(message.numMedia) > 0 && message.mediaUrl) {
                      return (
                        <div className="mt-2">
                          {message.mediaUrl.map((media, i) => (
                            <img
                              key={i}
                              src={media}
                              alt={`Attachment ${i + 1}`}
                              className="max-w-full rounded-lg cursor-pointer"
                              onClick={() => window.open(media, '_blank')}
                              onError={(e) => console.error('Failed to load image', media, e)}
                            />
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                  <div className={`flex items-center justify-end mt-1 text-xs ${
                    message.author === 'Store' ? 'text-blue-100' : 'text-gray-400'
                  }`}>
                    <span>{formatDateTime(message.dateCreated, true)}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reply Form */}
      <div className="p-4 bg-gray-50 border-t flex-shrink-0">
        <form onSubmit={handleSendReply} autoComplete="off">
          {/* Attached image previews */}
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-2">
              {attachments.map((att, i) => (
                <div key={i} className="relative w-16 h-16">
                  <img
                    src={att.previewUrl}
                    alt={`Attachment ${i + 1}`}
                    className="w-16 h-16 object-cover rounded-md border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={() => removeAttachment(i)}
                    title="Remove image"
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs leading-none flex items-center justify-center hover:bg-red-600"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={attachments.length >= 5}
              title="Attach image(s) — max 5, 10MB each"
              className="flex-shrink-0 bg-white text-gray-600 border border-gray-300 rounded-md px-3 py-2 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              +
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply here..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={!replyText.trim() && attachments.length === 0 || sending || uploading}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading…' : sending ? 'Sending…' : 'Send Reply'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
